"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Stack,
  Alert,
  alpha,
  useTheme,
  IconButton,
  Slide,
  SlideProps,
  FormControlLabel,
  Switch,
} from "@mui/material"
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  LockReset as LockResetIcon,
  PersonAdd as PersonAddIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"
import { createStaff_crm, getStaffList_crm } from "../../../api/users/user"
import axios from "axios"
import { requestID, baseUrl, config } from "../../../common/utils"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonIcon } from "@ionic/react"
import { add } from "ionicons/icons"
import React from "react"

interface Staff {
  id: number
  user_id: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  email: string | null
  loginStatus: number
  status: number
  createdAt: string
  roleName?: string
}

interface NewStaff {
  user_id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  role_id?: string
  username?: string
}

interface Role {
  id: string
  roleID: string
  name: string
  description?: string
}

interface AlertDialog {
  open: boolean
  title: string
  message: string
  type: "info" | "warning" | "error" | "success"
}

interface ConfirmationDialog {
  open: boolean
  title: string
  message: string
  action: () => Promise<void>
  loading?: boolean
}

const Transition = React.forwardRef(function Transition(
  props: SlideProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StaffManagementCRM_all: React.FC = () => {
  const theme = useTheme()
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditPasswordModalOpen, setIsEditPasswordModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null)
  
  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    staff: null as Staff | null,
    loading: false 
  })
  
  const [statusDialog, setStatusDialog] = useState({ 
    open: false, 
    staff: null as Staff | null,
    loading: false 
  })
  
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    staff: null as Staff | null,
    newPassword: "",
    confirmPassword: "",
    loading: false
  })
  
  const [alertDialog, setAlertDialog] = useState<AlertDialog>({
    open: false,
    title: "",
    message: "",
    type: "info"
  })

  const [newStaff, setNewStaff] = useState<NewStaff>({
    user_id: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    role_id: "",
    username: ""
  })

  // Color palette
  const colors = {
    primary: "#2196f3",
    secondary: "#ff4081",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#00bcd4",
    background: "#fafafa",
    card: "#ffffff",
    textPrimary: "#212121",
    textSecondary: "#757575",
  }

  useEffect(() => {
    fetchStaff()
    getRoles()
  }, [])

  const fetchStaff = async () => {
    try {
      setFetchLoading(true)
      const response = await getStaffList_crm()
      const data = await response.data
      
      const safeData = Array.isArray(data) 
        ? data.filter(staff => staff != null)
        : []
      
      setStaffList(safeData)
      showAlert("Staff list updated successfully", "success")
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to fetch staff", "error")
      console.error("Error fetching staff:", err)
    } finally {
      setFetchLoading(false)
    }
  }

  const getRoles = async () => {
    try {
      setRolesLoading(true)
      const formData = new FormData()
      formData.append("requestID", requestID())
      
      const response = await axios.post(
        baseUrl() + "businesss/getRoles_all",
        formData,
        config
      )

      const data = await response.data.data
      const safeRoles = Array.isArray(data) ? data : []
      setRoles(safeRoles)
    } catch (err) {
      console.error("Error fetching roles:", err)
      showAlert("Failed to load roles", "error")
    } finally {
      setRolesLoading(false)
    }
  }

  const handleAddStaff = async () => {
    if (
      !newStaff.firstName ||
      !newStaff.phone ||
      !newStaff.email ||
      !newStaff.password ||
      !newStaff.role_id
    ) {
      showAlert("Please fill in all required fields", "warning")
      return
    }

    setLoading(true)
    try {
      const res = await createStaff_crm(newStaff)
      
      if (res.status !== "true") {
        showAlert(res.message || "Failed to add staff member", "error")
        setLoading(false)
        return
      }

      await fetchStaff()
      setNewStaff({
        user_id: "",
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        password: "",
        role_id: "",
        username: ""
      })
      setIsModalOpen(false)
      showAlert("Staff member added successfully", "success")
    } catch (err) {
      showAlert(
        err instanceof Error ? err.message : "Failed to add staff",
        "error"
      )
      console.error("Error adding staff:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusToggle = (staff: Staff) => {
    setStatusDialog({ 
      open: true, 
      staff,
      loading: false 
    })
  }

  const confirmStatusToggle = async () => {
    if (!statusDialog.staff) return

    setStatusDialog(prev => ({ ...prev, loading: true }))
    
    try {
      const formData = new FormData()
      formData.append("requestID", requestID())
      formData.append("staff_id", statusDialog.staff.user_id)
      formData.append("action", isStaffActive(statusDialog.staff) ? "suspended" : "activate")
      
      const response = await axios.post(
        baseUrl() + "businesss/suspendStaff",
        formData,
        config
      )

      if (response.data.status) {
        await fetchStaff()
        showAlert(
          `Staff member ${isStaffActive(statusDialog.staff) ? "suspended" : "activated"} successfully`,
          "success"
        )
      } else {
        throw new Error("Failed to update status")
      }
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to update status", "error")
      console.error("Error updating status:", err)
    } finally {
      setStatusDialog({ open: false, staff: null, loading: false })
    }
  }

  const handleDeleteStaff = (staff: Staff) => {
    setDeleteDialog({ 
      open: true, 
      staff,
      loading: false 
    })
  }

  const confirmDeleteStaff = async () => {
    if (!deleteDialog.staff) return

    setDeleteDialog(prev => ({ ...prev, loading: true }))
    
    try {
      const formData = new FormData()
      formData.append("requestID", requestID())
      formData.append("user_id", deleteDialog.staff.user_id)
      
      const response = await axios.post(
        baseUrl() + "staffs/deleteStaff",
        formData,
        config
      )

      if (response.data.status) {
        await fetchStaff()
        showAlert("Staff member deleted successfully", "success")
      } else {
        throw new Error("Failed to delete staff")
      }
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to delete staff", "error")
      console.error("Error deleting staff:", err)
    } finally {
      setDeleteDialog({ open: false, staff: null, loading: false })
    }
  }

  const handleResetPassword = (staff: Staff) => {
    setPasswordDialog({
      open: true,
      staff,
      newPassword: "",
      confirmPassword: "",
      loading: false
    })
  }

  const confirmResetPassword = async () => {
    if (!passwordDialog.staff) return

    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      showAlert("Passwords do not match", "warning")
      return
    }

    if (passwordDialog.newPassword.length < 6) {
      showAlert("Password must be at least 6 characters long", "warning")
      return
    }

    setPasswordDialog(prev => ({ ...prev, loading: true }))
    
    try {
      const data = {
        requestID: requestID(),
        user_id: passwordDialog.staff.user_id,
        new_password: passwordDialog.newPassword
      }

      const response = await axios.post(
        baseUrl() + "users/updateStaffPassword",
        data,
        config
      )

      if (response.data.status) {
        showAlert("Password updated successfully", "success")
        setPasswordDialog({
          open: false,
          staff: null,
          newPassword: "",
          confirmPassword: "",
          loading: false
        })
      } else {
        throw new Error("Failed to update password")
      }
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to update password", "error")
      console.error("Error updating password:", err)
    } finally {
      setPasswordDialog(prev => ({ ...prev, loading: false }))
    }
  }

  // Helper functions
  const showAlert = (message: string, type: "info" | "warning" | "error" | "success", title?: string) => {
    setAlertDialog({
      open: true,
      title: title || type.charAt(0).toUpperCase() + type.slice(1),
      message,
      type
    })
  }

  const isStaffActive = (staff: Staff): boolean => {
    return staff.loginStatus === 1
  }

  const getStatusDisplay = (staff: Staff): string => {
    return isStaffActive(staff) ? "Active" : "Inactive"
  }

  const getStatusColor = (staff: Staff): "success" | "error" => {
    return isStaffActive(staff) ? "success" : "error"
  }

  const getInitials = (firstName: string | null, lastName: string | null): string => {
    const first = firstName?.charAt(0) || ''
    const last = lastName?.charAt(0) || ''
    return (first + last).toUpperCase() || 'U'
  }

  const getFullName = (firstName: string | null, lastName: string | null): string => {
    return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown User'
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  const getDialogIcon = (type: string) => {
    switch (type) {
      case "warning": return <WarningIcon sx={{ fontSize: 40, color: colors.warning }} />
      case "error": return <ErrorIcon sx={{ fontSize: 40, color: colors.error }} />
      case "success": return <CheckCircleIcon sx={{ fontSize: 40, color: colors.success }} />
      default: return <InfoIcon sx={{ fontSize: 40, color: colors.info }} />
    }
  }

  const getDialogColor = (type: string) => {
    switch (type) {
      case "warning": return colors.warning
      case "error": return colors.error
      case "success": return colors.success
      default: return colors.info
    }
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar style={{ background: colors.primary }}>
          <IonTitle style={{ color: 'white', fontWeight: 'bold' }}>
            Staff Management
          </IonTitle>
          <IonButton 
            slot="end" 
            onClick={() => setIsModalOpen(true)}
            style={{ 
              '--background': 'white', 
              '--color': colors.primary,
              '--border-radius': '8px',
              marginRight: '16px'
            } as any}
          >
            <IonIcon icon={add} slot="start" />
            Add Staff
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ background: colors.background, padding: '16px' }}>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4, mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Staff
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.success}, #66bb6a)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList.filter(staff => isStaffActive(staff)).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Staff
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.warning}, #ffb74d)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {staffList.filter(staff => !isStaffActive(staff)).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Inactive
                    </Typography>
                  </Box>
                  <BlockIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${colors.secondary}, #f06292)`,
              color: 'white',
              borderRadius: 2,
              boxShadow: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Refresh Data
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Update Staff List
                    </Typography>
                  </Box>
                  <RefreshIcon 
                    sx={{ 
                      fontSize: 40, 
                      opacity: 0.8, 
                      cursor: 'pointer',
                      '&:hover': { opacity: 1 }
                    }} 
                    onClick={fetchStaff}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Staff Table */}
        <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Staff Members
                </Typography>
                <Chip 
                  label={`${staffList.length} total`} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            }
            action={
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchStaff}
                disabled={fetchLoading}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                }}
              >
                {fetchLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            }
          />
          
          <CardContent sx={{ p: 0 }}>
            {fetchLoading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress sx={{ color: colors.primary }} />
                <Typography variant="body1" sx={{ mt: 2, color: colors.textSecondary }}>
                  Loading staff members...
                </Typography>
              </Box>
            ) : staffList.length === 0 ? (
              <Box textAlign="center" py={6}>
                <PersonIcon sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color={colors.textSecondary} gutterBottom>
                  No Staff Members
                </Typography>
                <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 3 }}>
                  Get started by adding your first staff member
                </Typography>
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  sx={{ background: colors.primary }}
                >
                  Add Staff Member
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Staff Member
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Contact Info
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Created Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffList.map((staff, index) => (
                      <TableRow 
                        key={`staff-${staff.user_id}-${index}`}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ 
                              bgcolor: colors.primary,
                              width: 40,
                              height: 40
                            }}>
                              {getInitials(staff.firstName, staff.lastName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {getFullName(staff.firstName, staff.lastName)}
                              </Typography>
                              {staff.roleName && (
                                <Typography variant="caption" color={colors.textSecondary}>
                                  {staff.roleName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {staff.phone || 'No phone'}
                          </Typography>
                          <Typography variant="caption" color={colors.textSecondary}>
                            {staff.email || 'No email'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={isStaffActive(staff)}
                                  onChange={() => handleStatusToggle(staff)}
                                  color="success"
                                  size="small"
                                />
                              }
                              label={
                                <Chip
                                  label={getStatusDisplay(staff)}
                                  size="small"
                                  sx={{
                                    background: isStaffActive(staff) ? colors.success : colors.error,
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                              }
                              sx={{ margin: 0 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(staff.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Reset Password">
                              <IconButton
                                size="small"
                                onClick={() => handleResetPassword(staff)}
                                sx={{ color: colors.info }}
                              >
                                <LockResetIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isStaffActive(staff) ? "Deactivate" : "Activate"}>
                              <IconButton
                                size="small"
                                onClick={() => handleStatusToggle(staff)}
                                sx={{ 
                                  color: isStaffActive(staff) ? colors.warning : colors.success 
                                }}
                              >
                                {isStaffActive(staff) ? <PauseIcon /> : <PlayArrowIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Staff">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteStaff(staff)}
                                sx={{ color: colors.error }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add Staff Modal */}
        <Dialog 
          open={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <PersonAddIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Add New Staff Member
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name *"
                  fullWidth
                  value={newStaff.firstName}
                  onChange={(e) => setNewStaff({ ...newStaff, firstName: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={newStaff.lastName}
                  onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone Number *"
                  fullWidth
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email *"
                  type="email"
                  fullWidth
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  required
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Username"
                  fullWidth
                  value={newStaff.username}
                  onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password *"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={newStaff.password}
                  onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                  required
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Role *</InputLabel>
                  <Select
                    value={newStaff.role_id}
                    onChange={(e) => setNewStaff({ ...newStaff, role_id: e.target.value })}
                    label="Role *"
                  >
                    <MenuItem value="">Select a role</MenuItem>
                    {roles?.map((role) => (
                      <MenuItem key={role.roleID} value={role.roleID}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setIsModalOpen(false)}
              variant="outlined"
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddStaff} 
              variant="contained" 
              disabled={loading}
              sx={{ 
                background: colors.primary,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.primary,
                  opacity: 0.9
                }
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Add Staff"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog 
          open={passwordDialog.open} 
          onClose={() => setPasswordDialog({ ...passwordDialog, open: false })} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: `linear-gradient(135deg, ${colors.info}, ${colors.primary})`,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <LockResetIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Reset Password
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color={colors.textSecondary}>
                Reset password for {passwordDialog.staff ? getFullName(passwordDialog.staff.firstName, passwordDialog.staff.lastName) : ''}
              </Typography>
            </Box>
            <TextField
              label="New Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={passwordDialog.newPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, newPassword: e.target.value })}
              required
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={passwordDialog.confirmPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, confirmPassword: e.target.value })}
              required
              variant="outlined"
            />
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setPasswordDialog({ ...passwordDialog, open: false })}
              variant="outlined"
              disabled={passwordDialog.loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmResetPassword} 
              variant="contained"
              disabled={!passwordDialog.newPassword || !passwordDialog.confirmPassword || passwordDialog.loading}
              sx={{ 
                background: colors.info,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.info,
                  opacity: 0.9
                }
              }}
            >
              {passwordDialog.loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Update Password"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: colors.error,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <DeleteIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Delete Staff Member
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ bgcolor: colors.primary, width: 60, height: 60 }}>
                {deleteDialog.staff && getInitials(deleteDialog.staff.firstName, deleteDialog.staff.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {deleteDialog.staff && getFullName(deleteDialog.staff.firstName, deleteDialog.staff.lastName)}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {deleteDialog.staff?.email}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {deleteDialog.staff?.phone}
                </Typography>
              </Box>
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                This action cannot be undone
              </Typography>
            </Alert>
            <Typography>
              Are you sure you want to permanently delete <strong>{deleteDialog.staff && getFullName(deleteDialog.staff.firstName, deleteDialog.staff.lastName)}</strong>? 
              This will remove all their data and access from the system.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}
              variant="outlined"
              disabled={deleteDialog.loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteStaff} 
              variant="contained"
              disabled={deleteDialog.loading}
              sx={{ 
                background: colors.error,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: colors.error,
                  opacity: 0.9
                }
              }}
            >
              {deleteDialog.loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Delete Permanently"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Toggle Dialog */}
        <Dialog
          open={statusDialog.open}
          onClose={() => setStatusDialog({ ...statusDialog, open: false })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: statusDialog.staff && isStaffActive(statusDialog.staff) ? colors.warning : colors.success,
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              {statusDialog.staff && isStaffActive(statusDialog.staff) ? 
                <PauseIcon sx={{ fontSize: 28 }} /> : 
                <PlayArrowIcon sx={{ fontSize: 28 }} />
              }
              <Typography variant="h6" fontWeight="bold">
                {statusDialog.staff && isStaffActive(statusDialog.staff) ? 'Deactivate Staff Member' : 'Activate Staff Member'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar sx={{ 
                bgcolor: statusDialog.staff && isStaffActive(statusDialog.staff) ? colors.warning : colors.success, 
                width: 60, height: 60 
              }}>
                {statusDialog.staff && getInitials(statusDialog.staff.firstName, statusDialog.staff.lastName)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {statusDialog.staff && getFullName(statusDialog.staff.firstName, statusDialog.staff.lastName)}
                </Typography>
                <Typography variant="body2" color={colors.textSecondary}>
                  {statusDialog.staff?.email}
                </Typography>
                <Chip
                  label={`Current Status: ${statusDialog.staff && getStatusDisplay(statusDialog.staff)}`}
                  size="small"
                  sx={{
                    background: statusDialog.staff ? (isStaffActive(statusDialog.staff) ? colors.success : colors.error) : colors.textSecondary,
                    color: 'white',
                    fontWeight: 500,
                    mt: 1
                  }}
                />
              </Box>
            </Box>
            
            {statusDialog.staff && isStaffActive(statusDialog.staff) ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Deactivating</strong> this staff member will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Revoke their system access immediately<br/>
                  • Prevent them from logging in<br/>
                  • Preserve all their data and records<br/>
                  • Can be reactivated at any time
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Activating</strong> this staff member will:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Restore their system access immediately<br/>
                  • Allow them to log in with their credentials<br/>
                  • Grant all their previous permissions<br/>
                  • Can be deactivated at any time
                </Typography>
              </Alert>
            )}

            <Typography variant="body2" color={colors.textSecondary}>
              {statusDialog.staff 
                ? `Are you sure you want to ${isStaffActive(statusDialog.staff) ? 'deactivate' : 'activate'} ${getFullName(statusDialog.staff.firstName, statusDialog.staff.lastName)}?`
                : ''
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setStatusDialog({ ...statusDialog, open: false })}
              variant="outlined"
              disabled={statusDialog.loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: colors.textSecondary,
                color: colors.textSecondary
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmStatusToggle} 
              variant="contained"
              disabled={statusDialog.loading}
              sx={{ 
                background: statusDialog.staff && isStaffActive(statusDialog.staff) ? colors.warning : colors.success,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: statusDialog.staff && isStaffActive(statusDialog.staff) ? colors.warning : colors.success,
                  opacity: 0.9
                }
              }}
            >
              {statusDialog.loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (statusDialog.staff && isStaffActive(statusDialog.staff) ? 'Deactivate Staff' : 'Activate Staff')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alert Dialog */}
        <Dialog
          open={alertDialog.open}
          onClose={() => setAlertDialog({ ...alertDialog, open: false })}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Transition}
          PaperProps={{
            sx: { 
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: getDialogColor(alertDialog.type),
            color: 'white',
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              {getDialogIcon(alertDialog.type)}
              <Typography variant="h6" fontWeight="bold">
                {alertDialog.title}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography>
              {alertDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setAlertDialog({ ...alertDialog, open: false })}
              variant="contained"
              sx={{ 
                background: getDialogColor(alertDialog.type),
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  background: getDialogColor(alertDialog.type),
                  opacity: 0.9
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  )
}

export default StaffManagementCRM_all